
import { defineComponent, computed, onMounted, ref, Ref } from 'vue';
import { useGettext } from '@jshmrtn/vue3-gettext';
import useSilentLogin from '@/composables/useSilentLogin';
import useNavigateTo from '@/composables/useNavigateTo';
import useHandleKey from '@/composables/useHandleKey';
import { Credentials } from '@/interfaces/credentials.interface';
import { CxServiceUnavailableError } from '@/classes/error.class';
import CxSpinner from '@/components/CxSpinner.vue';
import loginApi from '@/http/login.api';
import { ErrorType, useDisplayError } from '@/composables/useDisplayError';
import { useRoute } from 'vue-router';
import Shepherd from 'shepherd.js';
import { offset, shift } from '@floating-ui/dom';
import { sendAnalyticsEvent } from '../analytics';
import { analyticsEventNames } from '../analyticsEventNames.enum';

export default defineComponent({
  name: 'CxLogin',
  components: { CxSpinner },
  setup() {
    // Use composables and plugins
    const { isSilentLoginInProgress, isSilentRedirectInProgress, redirectToConX, silentLogin } = useSilentLogin();
    const { navigateToRecovery } = useNavigateTo();
    const { handleKeyDown } = useHandleKey();
    const language = useGettext();
    const { currentErrorType, errorText } = useDisplayError(language);
    const route = useRoute();

    // Data
    const username: Ref<string> = ref('');
    const password: Ref<string> = ref('');
    const rememberMe: Ref<boolean> = ref(true);
    const isLoginQueryInProgress: Ref<boolean> = ref(false);
    const showMyWorldLoginBtn: Ref<boolean> = ref(false);

    const floatingUIOptions = {
      middleware: [offset({ mainAxis: 16, crossAxis: 0 }), shift({ padding: 20 })],
    };
    const tourSteps: Shepherd.StepOptions[] = [
      {
        floatingUIOptions,
        attachTo: {
          element: '#my-world-login',
          on: 'top'
        },
        canClickTarget: false,
        classes: 'conx-tour-guide cx-login__conx-tour-guide',
        cancelIcon: { enabled: true, label: '' },
        buttons: [
          {
            action: () => {
              return Shepherd.activeTour?.complete();
            },
            text: 'Close',
            classes: 'next-button',
          }
        ],
        title: language.$gettext('Single Sign On with myWorld!'),
        text: language.$gettext('Tired of juggling multiple accounts for ConX companies? Manage all your ConX accounts effortlessly using a single login. Switch between companies with just one click and enjoy a seamless, hassle-free experience. Try login with myWorld today and simplify your ConX management!')
      }
    ];
    const tour = ref<Shepherd.Tour>(new Shepherd.Tour({
      useModalOverlay: true,
      steps: tourSteps,
    }));

    // Computed
    const translations = computed((): {
      welcome: string;
      username: string;
      password: string;
      remember: string;
      login: string;
      reset: string;
      myWorldLogin: string;
    } => {
      return {
        welcome: language.$gettext('Welcome to Leica ConX'),
        username: language.$gettext('Username'),
        password: language.$gettext('Password'),
        remember: language.$gettext('Remember me'),
        login: language.$gettext('Login'),
        reset: language.$gettext('Trouble logging in?'),
        myWorldLogin: language.$gettext('Login with myWorld')
      };
    });
    const canLogIn = computed((): boolean => {
      return !!username.value && !!password.value;
    });

    // Methods
    const onSilentLoginError = () => {
      currentErrorType.value = ErrorType.ServiceUnavailable;
    };
    const logIn = async (): Promise<void> => {
      try {
        isLoginQueryInProgress.value = true;
        const credentials: Credentials = {
          username: username.value,
          password: password.value,
          rememberMe: rememberMe.value
        };
        const result = await loginApi.logIn(credentials);
        if (!result) {
          currentErrorType.value = ErrorType.InvalidCredentials;
        } else {
          // eslint-disable-next-line @typescript-eslint/camelcase
          sendAnalyticsEvent(analyticsEventNames.USER_LOGGED_IN, { is_sso: false });
          redirectToConX();
        }
      } catch (error) {
        if (error instanceof CxServiceUnavailableError) currentErrorType.value = ErrorType.ServiceUnavailable;
        else currentErrorType.value = ErrorType.UnexpectedError;
      } finally {
        isLoginQueryInProgress.value = false;
      }
    };
    const checkMyWorldSwitch = (): void => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const tempWindow: any = window;
      const switches = tempWindow?.waffle?.SWITCHES;
      const myWorldParam = route.query.myworld;
      showMyWorldLoginBtn.value =
        (switches?.myworld_sso_login ?? false) || myWorldParam === 'true';
    };
    const navigateToMyWorld = (): void => {
      // eslint-disable-next-line @typescript-eslint/camelcase
      sendAnalyticsEvent(analyticsEventNames.USER_LOGGED_IN, { is_sso: true });
      window.location.href = `/sso/authenticate/?next=${process.env.VUE_APP_FMS_UI_LOCATION}`;
    };

    const saveGuideStatus = () => {
      localStorage.setItem('myworld-button-seen', 'true');
    };

    const checkGuideStatus = () => {
      return localStorage.getItem('myworld-button-seen') === 'true';
    };

    tour.value.on('complete', () => {
      saveGuideStatus();
    });

    tour.value.on('cancel', () => {
      saveGuideStatus();
    });

    // Lifecycle Hooks
    onMounted(async () => {
      silentLogin(onSilentLoginError);
      await checkMyWorldSwitch();
      if (showMyWorldLoginBtn.value && !checkGuideStatus()) {
        tour.value.start();
      }
    });

    // Expose properties and methods
    return {
      // Data
      username,
      password,
      rememberMe,
      isLoginQueryInProgress,
      isSilentLoginInProgress,
      isSilentRedirectInProgress,
      currentErrorType,
      // Computed
      translations,
      canLogIn,
      errorText,
      // Methods
      logIn,
      showMyWorldLoginBtn,
      navigateToMyWorld,
      navigateToRecovery,
      handleKeyDown
    };
  }
});
